import {
  ArrowLeft,
  Package,
  UserCheck,
  Lock,
  HelpCircle,
  Calendar,
  FileText,
  Users,
  Bell,
  Database,
  Activity,
} from 'react-feather';

import { ROLES } from '../../utils/jwt';

/**
 * Settings ("Inställningar") sidebar view.
 *
 * When a user navigates into the settings area the dashboard sidebar is swapped
 * for this menu. Each entry declares which roles may see it; entries without a
 * `roles` array are visible to every role. Destinations differ per role, so the
 * href is resolved through `hrefByRole` (falling back to the generic `href`).
 */

const BASE_PATH_BY_ROLE = {
  [ROLES.ADMIN]: '/admin',
  [ROLES.SALESPERSON]: '/salesperson',
  [ROLES.PRODUCTION]: '/produktion',
  [ROLES.CUSTOMER]: '/client',
  [ROLES.PARTNER]: '/partner',
  [ROLES.USER]: '/user',
};

export const HOME_PATH_BY_ROLE = {
  [ROLES.ADMIN]: '/admin/dashboard/default',
  [ROLES.SALESPERSON]: '/salesperson/dashboard/default',
  [ROLES.PRODUCTION]: '/produktion/produktionsvy',
  [ROLES.CUSTOMER]: '/client/dashboard',
  [ROLES.PARTNER]: '/partner/leads',
  [ROLES.USER]: '/user/dashboard/default',
};

const getBasePath = (role) => BASE_PATH_BY_ROLE[role] || '/user';

/**
 * The settings entries in display order. `roles` omitted means "all roles",
 * unless `excludedRoles` lists roles that should not see the entry.
 * `hrefByRole` maps a role to its destination; entries only reachable for the
 * listed roles simply omit the others.
 */
const SETTINGS_ENTRIES = [
  {
    key: 'produktregister',
    title: 'Produktregister',
    icon: Package,
    roles: [ROLES.ADMIN, ROLES.PRODUCTION],
    hrefByRole: {
      [ROLES.ADMIN]: '/admin/drift/produkter',
      [ROLES.PRODUCTION]: '/produktion/produkter',
    },
  },
  {
    key: 'anvandare-roller',
    title: 'Användare och roller',
    icon: UserCheck,
    roles: [ROLES.ADMIN],
    hrefByRole: {
      [ROLES.ADMIN]: '/admin/drift/anvandare',
    },
  },
  {
    key: 'losenord',
    title: 'Byt lösen',
    icon: Lock,
    href: (base) => `${base}/settings/losenord`,
  },
  {
    key: 'guider',
    title: 'Guider & Introduktion',
    icon: HelpCircle,
    href: (base) => `${base}/settings/guider`,
  },
  {
    key: 'integrationer',
    title: 'Google Calendar-synk',
    icon: Calendar,
    excludedRoles: [ROLES.CUSTOMER],
    href: (base) => `${base}/settings/integrationer`,
  },
  {
    key: 'dpa',
    title: 'DPA Avtal',
    icon: FileText,
    roles: [ROLES.ADMIN],
    hrefByRole: {
      [ROLES.ADMIN]: '/admin/dpa',
    },
  },
  {
    key: 'anvandare',
    title: 'Användare',
    icon: Users,
    roles: [ROLES.ADMIN, ROLES.PRODUCTION],
    hrefByRole: {
      [ROLES.ADMIN]: '/admin/users',
      [ROLES.PRODUCTION]: '/produktion/users',
    },
  },
  {
    key: 'notifikationer',
    title: 'Notifikationsinställningar',
    icon: Bell,
    excludedRoles: [ROLES.CUSTOMER],
    href: (base) => `${base}/settings/notifikationer`,
  },
  {
    key: 'datahantering',
    title: 'Datahantering',
    icon: Database,
    roles: [ROLES.ADMIN, ROLES.PRODUCTION],
    childrenByRole: {
      [ROLES.ADMIN]: [
        { href: '/admin/data-management/cities', title: 'Städer' },
        { href: '/admin/data-management/blocklist', title: 'Blocklista' },
      ],
      [ROLES.PRODUCTION]: [
        { href: '/produktion/data-management/cities', title: 'Städer' },
        { href: '/produktion/data-management/blocklist', title: 'Blocklista' },
      ],
    },
    hrefByRole: {
      [ROLES.ADMIN]: '/admin/data-management/cities',
      [ROLES.PRODUCTION]: '/produktion/data-management/cities',
    },
  },
  {
    key: 'svea',
    title: 'Svea status',
    icon: Activity,
    roles: [ROLES.ADMIN],
    hrefByRole: {
      [ROLES.ADMIN]: '/admin/drift/svea-status',
    },
  },
  {
    key: 'fortnox',
    title: 'Fortnox status',
    icon: Activity,
    roles: [ROLES.ADMIN],
    hrefByRole: {
      [ROLES.ADMIN]: '/admin/drift/fortnox-status',
    },
  },
  {
    key: 'rebuild-all',
    title: 'Rebuild-all status',
    icon: Activity,
    roles: [ROLES.ADMIN],
    hrefByRole: {
      [ROLES.ADMIN]: '/admin/drift/rebuild-status',
    },
  },
];

const isVisibleForRole = (entry, role) => {
  if (entry.excludedRoles && entry.excludedRoles.includes(role)) return false;
  return !entry.roles || entry.roles.includes(role);
};

const resolveHref = (entry, role, base) => {
  if (entry.hrefByRole && entry.hrefByRole[role]) return entry.hrefByRole[role];
  if (typeof entry.href === 'function') return entry.href(base);
  return entry.href || null;
};

const resolveChildren = (entry, role) => {
  if (!entry.childrenByRole) return undefined;
  return entry.childrenByRole[role];
};

const buildPages = (role) => {
  const base = getBasePath(role);
  return SETTINGS_ENTRIES.filter((entry) => isVisibleForRole(entry, role))
    .map((entry) => {
      const children = resolveChildren(entry, role);
      return {
        href: resolveHref(entry, role, base),
        title: entry.title,
        icon: entry.icon,
        ...(children ? { children } : {}),
      };
    })
    .filter((page) => page.href || page.children);
};

/**
 * Build the settings sidebar sections for a given role. The first section is a
 * "back to the main menu" shortcut, the second holds the role-filtered entries.
 */
export const getSettingsItems = (role) => [
  {
    title: '',
    pages: [
      {
        href: HOME_PATH_BY_ROLE[role] || '/user/dashboard/default',
        title: 'Till huvudmenyn',
        icon: ArrowLeft,
      },
    ],
  },
  {
    title: 'Inställningar',
    pages: buildPages(role),
  },
];

/**
 * Collect every destination reachable from the settings menu for a role. Used to
 * decide whether the sidebar should render the settings view for the active URL.
 */
const collectSettingsPaths = (role) => {
  const base = getBasePath(role);
  const paths = [`${base}/settings`];
  buildPages(role).forEach((page) => {
    if (page.href) paths.push(page.href);
    if (page.children) {
      page.children.forEach((child) => {
        if (child.href) paths.push(child.href);
      });
    }
  });
  return paths;
};

/**
 * True when the active pathname belongs to the settings area for the role, so
 * the dashboard layout can swap to the settings sidebar.
 */
export const isSettingsPath = (pathname, role) => {
  if (!pathname) return false;
  const base = getBasePath(role);
  if (pathname.startsWith(`${base}/settings`)) return true;
  return collectSettingsPaths(role).some((path) => pathname.startsWith(path));
};

export default getSettingsItems;
